import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { StoryThumbnailsStateModel } from './story.thumbnails.state.model';
import {
  RegisterChapterThumbnails,
  TakeChapterPicture,
  TakeChapterPictureSuccess,
} from './story.thumbnails.actions';
import { appDefaultBackground } from 'src/app/dialogs/components/background-color-dialog/background-config';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { SnapshotConfig } from 'src/app/engine/models/snapshot-config.model';
import { ColorUtils } from 'src/app/shared/libraries/colors/colors';
import { ImageTypeEnum } from 'src/app/shared/models/image-type.enum';
import { ThumbnailConstants } from 'src/app/shared/thumbnail.constants';
import { StoryChapterThumbnail } from '../../models/story-chapter-thumbnail.model';
import { StoryChaptersStateModel } from '../chapters/story.chapters.state.model';
import { SnapshotService } from 'src/app/engine/snapshot.service';

declare const immer;

// snapshot config
const defaultSnapshotConfig: SnapshotConfig = {
  imageType: ImageTypeEnum.JPEG,
  crop: 'original',

  copyrightEnabled: false,
  copyrightImagePath: 'assets/images/powered-by-biodigital-color.svg',
  copyrightPosition: {
    x: 'left',
    y: 'bottom',
  },
  copyrightOffset: {
    x: 12,
    y: -12,
  },
  imageQuality: 1.0,
};

const initialState: StoryThumbnailsStateModel = {
  thumbnails: new Map(),
};

@State<StoryThumbnailsStateModel>({
  name: 'storyThumbnails',
  defaults: initialState,
})
@Injectable()
export class StoryThumbnailsState {
  /**
   * Selectors
   */
  @Selector()
  static getRegisteredThumbnails(state: StoryThumbnailsStateModel) {
    return state.thumbnails;
  }

  /**
   * Actions
   */

  @Action(RegisterChapterThumbnails)
  registerChapterThumbnails(
    context: StateContext<StoryThumbnailsStateModel>,
    action: RegisterChapterThumbnails,
  ) {
    // urls that came with scene for managing and housekeeping of server thumbnails per chapter
    context.patchState(
      immer.produce(context.getState(), (draft) => {
        draft.thumbnails = new Map(action.thumbnails);
      }),
    );
  }

  @Action(TakeChapterPicture)
  takeChapterPicture(context: StateContext<StoryChaptersStateModel>, action: TakeChapterPicture) {
    const {
      chapterId,
      includeLabels,
      includeCopyright,
      dimensions,
      imageType,
      imageQuality,
      crop,
      layers,
    } = action;
    // get target dimensions: fallback to default
    const snapshotDimensions = dimensions ? dimensions : ThumbnailConstants.large;
    const { width, height } = snapshotDimensions;

    // get selected background
    const background = action.background;

    let snapshotBackground;
    switch (imageType) {
      case ImageTypeEnum.PNG:
        snapshotBackground = background ? background : appDefaultBackground;
        break;
      case ImageTypeEnum.JPEG:
      default:
        snapshotBackground =
          background && !BackgroundModel.isTransparent(background)
            ? background
            : appDefaultBackground;
        break;
    }

    // get logo based on background color
    const outerRGB = background.colors.slice(0, 3) as [number, number, number];
    const isLight = ColorUtils.isLight(outerRGB);

    const snapshotConfig: SnapshotConfig = {
      ...defaultSnapshotConfig,
      crop: crop,
      imageType: imageType,
      imageQuality: imageQuality,
      copyrightEnabled: includeCopyright,
      copyrightImagePath: isLight
        ? 'assets/images/powered-by-biodigital-color.svg'
        : 'assets/images/powered-by-biodigital-white.svg',
    };

    setTimeout(() => {
      this.snapshotService
        .takeSnapshot(width, height, snapshotBackground, layers, includeLabels, snapshotConfig)
        .subscribe((blob) => {
          context.dispatch(new TakeChapterPictureSuccess(chapterId, blob, snapshotDimensions));
        });
    });
  }

  @Action(TakeChapterPictureSuccess)
  takeChapterPictureSuccess(
    context: StateContext<StoryChaptersStateModel>,
    action: TakeChapterPictureSuccess,
  ) {
    context.patchState(
      immer.produce(context.getState(), (draft) => {
        const thumbnail = draft.thumbnails.get(action.chapterId) || new StoryChapterThumbnail();
        thumbnail.snapshot = URL.createObjectURL(action.blob);
        draft.thumbnails.set(action.chapterId, thumbnail);
      }),
    );
  }

  constructor(private snapshotService: SnapshotService, private store: Store) {}
}
